import { BitkitAlert, BitkitButton, BitkitDialog, createBitkitToast, IconHistory } from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Text } from '@chakra-ui/react/text';
import { JSX, ReactNode, useState } from 'react';
import { useMediaQuery } from 'usehooks-ts';

import ConfirmationDialog from '@/components/Dialog/ConfirmationDialog/ConfirmationDialog';
import FileViewerSkeleton from '@/components/FileViewer/components/FileViewerSkeleton';
import FileViewer from '@/components/FileViewer/FileViewer';
import {
  useAppBitriseYml,
  useBuildBitriseYml,
  usePipelineBitriseYml,
  useRestoreAppBitriseYml,
} from '@/hooks/bitriseYml/useBitriseYml';
import formatTimestamp from '@/pages/BuildDetailsPage/utils/formatTimestamp';

type BaseShowYmlDialogProps = {
  appSlug: string;
  buildNumber: number;
  canRestoreYml: boolean;
  isVisible: boolean;
  onClose: VoidFunction;
  render?: (oldContent: string, newContent?: string) => JSX.Element;
  triggeredAt?: string | null;
};

type BuildSpecificShowYmlDialogProps = BaseShowYmlDialogProps & {
  sourceType: 'build';
  buildSlug: string;
  configDatastoreUpdatedAt?: string | null;
};

type PipelineSpecificShowYmlDialogProps = BaseShowYmlDialogProps & {
  sourceType: 'pipeline';
  pipelineId: string;
  attemptId: string;
};

type ShowYMLDialogProps = BuildSpecificShowYmlDialogProps | PipelineSpecificShowYmlDialogProps;

type ConfigPaneHeadingProps = {
  children?: ReactNode;
  heading: string;
};

const ConfigPaneHeading = ({ children, heading }: ConfigPaneHeadingProps) => (
  <Box display="flex" flexDirection="column" gap="4" flex={1} minWidth={0}>
    {/* The dialog title is the h2, so these sit one level under it and stay navigable by heading. */}
    <Text as="h3" textStyle="heading/h4" color="text/primary">
      {heading}
    </Text>
    {children}
  </Box>
);

const LABELED_TEXT_STYLES = {
  lg: { color: 'text/body', label: 'body/lg/semibold', value: 'body/lg/regular' },
  sm: { color: 'text/secondary', label: 'body/sm/semibold', value: 'body/sm/regular' },
} as const;

type LabeledTextProps = {
  label: string;
  size?: keyof typeof LABELED_TEXT_STYLES;
  value?: string;
};

const LabeledText = ({ label, size = 'sm', value }: LabeledTextProps) => {
  if (!value) {
    return null;
  }

  const styles = LABELED_TEXT_STYLES[size];

  return (
    <Text textStyle={styles.value} color={styles.color}>
      <Text as="span" textStyle={styles.label}>
        {label}
      </Text>{' '}
      {value}
    </Text>
  );
};

const ShowYmlDialog = (props: ShowYMLDialogProps) => {
  // Common props and hooks
  const {
    appSlug,
    sourceType,
    buildNumber,
    isVisible,
    onClose,
    canRestoreYml,
    triggeredAt,
    render = (sourceYml, baseYml) => <FileViewer language="yaml" content={sourceYml} diffContent={baseYml} />,
  } = props;

  const [showConfirmRestoreYml, setShowConfirmRestoreYml] = useState(false);
  const appYml = useAppBitriseYml({ appSlug, enabled: isVisible });
  const restoreYml = useRestoreAppBitriseYml({ appSlug });
  const isUnifiedDiffView = useMediaQuery('(max-width: 1028px)');

  // Get specific props and hooks based on sourceType discriminator
  const { buildSlug, pipelineId, attemptId } =
    sourceType === 'build'
      ? {
          ...props,
          pipelineId: '',
          attemptId: '',
        }
      : { ...props, buildSlug: '' };
  const buildYml = useBuildBitriseYml({ buildSlug, enabled: isVisible });
  const pipelineYml = usePipelineBitriseYml({ appSlug, pipelineId, attemptId, enabled: isVisible });

  // Calculated fields
  const sourceTypeYml = sourceType === 'build' ? buildYml : pipelineYml;
  const currentYml = appYml.data?.yml;
  const hasYmlDifference = currentYml && sourceTypeYml.data && currentYml !== sourceTypeYml.data;
  const isLoading = appYml.isLoading || sourceTypeYml.isLoading;

  // Save times are recorded for builds only, so a pipeline shows neither side's rather than a lone
  // timestamp over the most recent config with nothing to compare it against.
  const isBuild = props.sourceType === 'build';
  const triggeredAtLabel = formatTimestamp(triggeredAt);
  const usedConfigSavedAt = isBuild ? formatTimestamp(props.configDatastoreUpdatedAt) : undefined;
  const currentConfigSavedAt = isBuild ? formatTimestamp(appYml.data?.updatedAt) : undefined;

  const usedConfigHeading = `Configuration used in this ${sourceType}`;
  const usedConfigSavedAtLabel = `Configuration last saved before ${sourceType} was triggered:`;

  const confirmRestoreYml = () => {
    if (!sourceTypeYml.data) {
      return;
    }

    restoreYml.mutate(sourceTypeYml.data, {
      onSuccess: () => {
        setShowConfirmRestoreYml(false);
        createBitkitToast({
          messageText: `This ${sourceType}’s configuration YAML version was restored successfully.`,
          titleText: 'Configuration YAML restored',
          variant: 'success',
        });
        onClose();
      },
      onError: (error) => {
        createBitkitToast({
          messageText: error.message,
          titleText: 'Failed to restore configuration YAML',
          variant: 'critical',
        });
      },
    });
  };

  const renderHeadings = () => {
    if (hasYmlDifference && !isUnifiedDiffView) {
      return (
        // No gap: each heading is half the row, so it lines up with the diff pane below it.
        <Box display="flex">
          <ConfigPaneHeading heading={usedConfigHeading}>
            <LabeledText label={usedConfigSavedAtLabel} value={usedConfigSavedAt} />
          </ConfigPaneHeading>
          <ConfigPaneHeading heading="Most recent configuration">
            <LabeledText label="Last saved at:" value={currentConfigSavedAt} />
          </ConfigPaneHeading>
        </Box>
      );
    }

    if (hasYmlDifference) {
      return (
        // Stacked under one heading, so "Last saved at" has no pane title to name its subject.
        <ConfigPaneHeading
          heading={`The configuration used in this ${sourceType} differs from the most recent configuration`}
        >
          <LabeledText label={usedConfigSavedAtLabel} value={usedConfigSavedAt} />
          <LabeledText label="Most recent configuration last saved at:" value={currentConfigSavedAt} />
        </ConfigPaneHeading>
      );
    }

    if (currentYml && sourceTypeYml.data) {
      return (
        <ConfigPaneHeading heading={`The ${sourceType} used the most recent configuration`}>
          <LabeledText label="Last saved at:" value={currentConfigSavedAt} />
        </ConfigPaneHeading>
      );
    }

    // The current config failed to load, so only the one this build ran with is on screen.
    return sourceTypeYml.data ? <ConfigPaneHeading heading={usedConfigHeading} /> : null;
  };

  return (
    <BitkitDialog
      data-testid="show-yaml-dialog"
      footerButtons={
        <>
          {!isLoading && hasYmlDifference && canRestoreYml && (
            <BitkitButton
              variant="danger-secondary"
              icon={IconHistory}
              marginInlineEnd="auto"
              onClick={() => setShowConfirmRestoreYml(true)}
            >
              Restore configuration YAML
            </BitkitButton>
          )}
          <BitkitButton variant="secondary" onClick={onClose}>
            Close
          </BitkitButton>
        </>
      }
      open={isVisible}
      size="full"
      title={`${sourceType === 'build' ? 'Build' : 'Pipeline'} #${buildNumber} - Configuration YAML`}
      onOpenChange={({ open }) => !open && onClose()}
    >
      <LabeledText label="Triggered at:" size="lg" value={triggeredAtLabel} />
      {appYml.isError && (
        <BitkitAlert
          variant="critical"
          action={{ label: 'Retry', onClick: appYml.refetch, state: appYml.isFetching ? 'loading' : undefined }}
          data-testid="error-for-loading-current-yml"
          messageText="Failed to load the current configuration for the project."
        />
      )}
      {buildYml.isError && (
        <BitkitAlert
          variant="critical"
          action={{ label: 'Retry', onClick: buildYml.refetch, state: buildYml.isFetching ? 'loading' : undefined }}
          data-testid="error-for-loading-builds-yml"
          messageText="Failed to load configuration YAML of this build"
        />
      )}
      {pipelineYml.isError && (
        <BitkitAlert
          variant="critical"
          action={{
            label: 'Retry',
            onClick: pipelineYml.refetch,
            state: pipelineYml.isFetching ? 'loading' : undefined,
          }}
          data-testid="error-for-loading-pipeline-yml"
          messageText="Failed to load configuration YAML of this pipeline"
        />
      )}
      {isLoading && <FileViewerSkeleton />}
      {!isLoading && (
        <Box display="flex" flexDirection="column" gap="24" flexGrow={1} minWidth="0" minHeight={0}>
          {renderHeadings()}

          <Box
            display="flex"
            flexDirection="column"
            gap="8"
            flexGrow={1}
            minWidth="0"
            overflow="auto"
            position="relative"
            height={['100dvh', '100%']}
          >
            {sourceTypeYml.data && render(sourceTypeYml.data, currentYml)}
          </Box>
        </Box>
      )}
      <ConfirmationDialog
        isVisible={showConfirmRestoreYml}
        isInProgress={restoreYml.isPending}
        onClose={() => {
          setShowConfirmRestoreYml(false);
          restoreYml.reset();
        }}
        title="Restore configuration YAML?"
        text={`Are you sure you want to overwrite the saved configuration YAML with the ${sourceType}'s configuration YAML? All the changes will be lost.`}
        confirmText="Overwrite"
        closeText="Cancel"
        onConfirm={confirmRestoreYml}
      />
    </BitkitDialog>
  );
};

export default ShowYmlDialog;
